import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserMinus, UserPlus, GraduationCap, Calendar, Save, Search, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { useStudentsList } from "@/hooks/useStudentsList";
import { useToast } from "@/hooks/use-toast";
import { getStudentSchedule, updateStudentSchedule } from "@/hooks/useParentEnrollmentWithSchedule";
import { useActivityEnrollment } from "@/hooks/useActivityEnrollment";

interface ActivityStudentsListProps {
  activity: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ActivityStudentsList = ({ activity, open, onOpenChange }: ActivityStudentsListProps) => {
  const [studentSchedules, setStudentSchedules] = useState<Record<string, number[]>>({});
  const [scheduleChanges, setScheduleChanges] = useState<Record<string, number[]>>({});
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [expandedSchedules, setExpandedSchedules] = useState<Record<string, boolean>>({});
  const { students = [], isLoading: studentsLoading, refetch: refetchStudents } = useStudentsList();
  const { enrollStudent, unenrollStudent } = useActivityEnrollment();
  const { toast } = useToast();

  // Database stores ISO standard: 1=Monday, 2=Tuesday, ..., 5=Friday, 6=Saturday, 0=Sunday
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']; // Index 0=Sunday, 1=Monday, etc.

  
  // Estudiantes inscritos en esta actividad (comparación forzada a string)
  const enrolledStudents = students.filter(student => {
    if (!student.student_activities) return false;
    return student.student_activities.some((sa: any) => {
      const activityMatch = String(sa.activity_id) === String(activity.id);
      const statusMatch = sa.status === 'active';
      return activityMatch && statusMatch;
    });
  });

  // Estudiantes no inscritos
  const availableStudents = students.filter(student => {
    if (!student.student_activities) return true;
    return !student.student_activities.some((sa: any) => 
      String(sa.activity_id) === String(activity.id) && sa.status === 'active'
    );
  });

  // Refrescar la lista cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      refetchStudents();
    }
  }, [open, refetchStudents]);

  const loadStudentSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const schedules: Record<string, number[]> = {};
      
      // Load schedules for ALL enrolled students, regardless of requires_day_selection
      for (const student of enrolledStudents) {
        const schedule = await getStudentSchedule(student.id, activity.id);
        schedules[student.id] = schedule;
      }
      
      setStudentSchedules(schedules);
      setScheduleChanges({}); // Reset changes
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los horarios.",
        variant: "destructive"
      });
    } finally {
      setLoadingSchedules(false);
    }
  };

  // Cargar horarios cuando hay estudiantes inscritos
  useEffect(() => {
    if (open && enrolledStudents.length > 0) {
      loadStudentSchedules();
    }
  }, [open, enrolledStudents.length]);


  const handleEnrollStudent = async (studentId: string) => {
    try {
      setLoading(true);
      await enrollStudent.mutateAsync({ studentId, activityId: activity.id });
      refetchStudents();
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrollStudent = async (studentId: string) => {
    try {
      setLoading(true);
      await unenrollStudent.mutateAsync({ studentId, activityId: activity.id });
      refetchStudents();
      // Always reload schedules after unenrolling
      loadStudentSchedules();
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChange = (studentId: string, day: number, checked: boolean) => {
    const currentSchedule = scheduleChanges[studentId] || studentSchedules[studentId] || [];
    const newSchedule = checked 
      ? [...currentSchedule, day].sort()
      : currentSchedule.filter(d => d !== day);
    
    setScheduleChanges(prev => ({
      ...prev,
      [studentId]: newSchedule
    }));
  };

  const saveScheduleChanges = async (studentId: string) => {
    const newSchedule = scheduleChanges[studentId];
    if (!newSchedule) return;

    try {
      setLoading(true);
      await updateStudentSchedule(studentId, activity.id, newSchedule);
      
      setStudentSchedules(prev => ({
        ...prev,
        [studentId]: newSchedule
      }));
      
      setScheduleChanges(prev => {
        const updated = { ...prev };
        delete updated[studentId];
        return updated;
      });

      toast({
        title: "Horario actualizado",
        description: "Los días de la actividad han sido actualizados correctamente."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el horario.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStudentSchedule = (student: any) => {
    const studentSchedule = studentSchedules[student.id] || [];
    const isExpanded = expandedSchedules[student.id];
    
    // If student has no specific schedule, show all activity days as fallback
    const displayDays = studentSchedule.length > 0 ? studentSchedule : (activity?.days_of_week || []);
    
    // For activities that don't require day selection, show read-only badges
    if (!activity?.requires_day_selection) {
      return (
        <div className="flex flex-wrap gap-1 mt-2">
          <span className="text-xs text-muted-foreground mr-1">Días:</span>
          {displayDays.map((day: number) => (
            <Badge key={day} variant="secondary" className="text-xs">
              {dayNames[day]}
            </Badge>
          ))}
          {studentSchedule.length === 0 && (
            <Badge variant="outline" className="text-xs">
              Todos los días
            </Badge>
          )}
        </div>
      );
    }

    // For activities that require day selection, show interactive schedule editor
    const currentSchedule = scheduleChanges[student.id] || studentSchedule;
    const hasChanges = scheduleChanges[student.id] !== undefined;

    // Vista compacta por defecto
    if (!isExpanded) {
      return (
        <div className="flex flex-wrap gap-1 mt-2">
          <span className="text-xs text-muted-foreground mr-1">Días:</span>
          {currentSchedule.map((day: number) => (
            <Badge key={day} variant={hasChanges ? "default" : "secondary"} className="text-xs">
              {dayNames[day]}
            </Badge>
          ))}
          {currentSchedule.length === 0 && (
            <Badge variant="outline" className="text-xs">
              Sin días asignados
            </Badge>
          )}
          {hasChanges && (
            <Badge variant="destructive" className="text-xs">
              Sin guardar
            </Badge>
          )}
        </div>
      );
    }

    // Vista expandida para edición
    return (
      <Card className="mt-2 p-2 bg-muted/30">
        <CardContent className="p-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Días programados:
            </span>
            {hasChanges && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => saveScheduleChanges(student.id)}
                disabled={loading}
                className="h-6 text-xs"
              >
                <Save className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {activity?.days_of_week?.map((day: number) => {
              const isSelected = currentSchedule.includes(day);
              return (
                <div key={day} className="flex flex-col items-center">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => 
                      handleScheduleChange(student.id, day, checked as boolean)
                    }
                    disabled={loadingSchedules}
                    className="h-3 w-3"
                  />
                  <span className="text-xs mt-1">{dayNames[day]}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Filtrar estudiantes por búsqueda y clase
  const filteredEnrolledStudents = enrolledStudents.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = classFilter === "all" || student.class === classFilter;
    return matchesSearch && matchesClass;
  });

  const filteredAvailableStudents = availableStudents.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = classFilter === "all" || student.class === classFilter;
    return matchesSearch && matchesClass;
  });

  // Obtener clases únicas para el filtro
  const uniqueClasses = [...new Set(students.map(s => s.class))].sort();

  const toggleScheduleExpansion = (studentId: string) => {
    setExpandedSchedules(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] w-[90vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            Alumnos - {activity.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-full">
          {/* Header fijo con estadísticas y filtros */}
          <div className="sticky top-0 bg-background border-b pb-4 mb-4 space-y-4">
            {/* Estadísticas */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-primary">{enrolledStudents.length}</div>
                <div className="text-sm text-muted-foreground">Inscritos</div>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold">{activity.maxStudents || 20}</div>
                <div className="text-sm text-muted-foreground">Capacidad</div>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-success">{(activity.maxStudents || 20) - enrolledStudents.length}</div>
                <div className="text-sm text-muted-foreground">Disponibles</div>
              </div>
            </div>

            {/* Filtros y búsqueda */}
            <div className="flex gap-4 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar estudiantes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={classFilter} onValueChange={setClassFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar por clase" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las clases</SelectItem>
                  {uniqueClasses.map((className) => (
                    <SelectItem key={className} value={className}>
                      {className}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Layout de dos columnas en pantallas grandes */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0">
            {/* Estudiantes inscritos */}
            <div className="flex flex-col">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                Estudiantes Inscritos ({filteredEnrolledStudents.length}/{enrolledStudents.length})
              </h3>
              <ScrollArea className="flex-1 min-h-[300px] max-h-[500px] border rounded-lg p-3">
                {filteredEnrolledStudents.length > 0 ? (
                  <div className="space-y-2">
                    {filteredEnrolledStudents.map((student: any) => (
                      <div key={student.id} className="p-3 bg-card rounded-lg border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="text-xs">
                                {student.name.split(' ').map((n: string) => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium text-sm">{student.name}</div>
                              <div className="text-xs text-muted-foreground">{student.class}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {activity?.requires_day_selection && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleScheduleExpansion(student.id)}
                                className="h-8 w-8 p-0"
                              >
                                {expandedSchedules[student.id] ? 
                                  <ChevronUp className="w-4 h-4" /> : 
                                  <ChevronDown className="w-4 h-4" />
                                }
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnenrollStudent(student.id)}
                              disabled={loading}
                              className="h-8"
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {renderStudentSchedule(student)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    {searchTerm || classFilter !== "all" ? "No se encontraron estudiantes" : "No hay estudiantes inscritos"}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Estudiantes disponibles */}
            <div className="flex flex-col">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                Estudiantes Disponibles ({filteredAvailableStudents.length}/{availableStudents.length})
              </h3>
              <ScrollArea className="flex-1 min-h-[300px] max-h-[500px] border rounded-lg p-3">
                {filteredAvailableStudents.length > 0 ? (
                  <div className="space-y-2">
                    {filteredAvailableStudents.map((student: any) => (
                      <div key={student.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="text-xs">
                              {student.name.split(' ').map((n: string) => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium text-sm">{student.name}</div>
                            <div className="text-xs text-muted-foreground">{student.class}</div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleEnrollStudent(student.id)}
                          disabled={loading || enrolledStudents.length >= (activity.maxStudents || 20)}
                          className="h-8"
                        >
                          <UserPlus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    {searchTerm || classFilter !== "all" ? "No se encontraron estudiantes" : "Todos los estudiantes están inscritos"}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};